import React, { useEffect, useState } from "react";
import { AttributeWindowModel, LocaleModel, Locales } from "../api/drawingApi";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import {
  glossaryApi,
  LocalizedAttributeWindowTextModel,
} from "../api/glossaryApi";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";

interface TranslationsModalProps {
  attributeWindows: AttributeWindowModel[];
  onClose: () => void;
}

interface TranslatedText {
  locale: LocaleModel | undefined;
  titles: string[];
  texts: string[];
}

function TranslationModal({
  attributeWindows,
  onClose,
}: TranslationsModalProps) {
  const [error, setError] = useState<Problem>();
  const [currentLocale, setCurrentLocale] = useState<LocaleModel>();
  const [translations, setTranslations] = useState<TranslatedText[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadTranslations() {
      setLoading(true);
      let titleTemplates: string[] = [];
      let textTemplates: string[] = [];

      let translations: TranslatedText[] = [];

      // first get the templates (normalized)
      const attributeWindowTexts = attributeWindows.map(
        (aw) =>
          new LocalizedAttributeWindowTextModel(
            aw.title ?? "",
            aw.text?.replaceAll(/(?:\r\n|\r|\n)/g, "<NL>") ?? "",
            LocaleModel.English,
          ),
      );

      const templateResult = await glossaryApi.getTemplatedAttributeWindows(
        undefined,
        attributeWindowTexts,
      );

      if (!templateResult.ok) {
        setError(templateResult.problem);
        setLoading(false);
        return;
      }

      for (let ii = 0; ii < templateResult.value.templates.length; ++ii) {
        titleTemplates.push(templateResult.value.templates[ii].title);
        textTemplates.push(templateResult.value.templates[ii].text);
      }

      // we want to see the text with newlines
      translations.push({
        locale: undefined,
        titles: titleTemplates,
        texts: textTemplates.map((tt) => tt.replaceAll("<NL>", "\r\n")),
      });

      // now get the translations
      const translationResults =
        await glossaryApi.getTranslatedAttributeWindows(
          templateResult.value.templates,
          Locales,
        );

      setLoading(false);

      if (!translationResults.ok) {
        setError(translationResults.problem);
        return;
      }

      for (let locale = 0; locale < Locales.length; ++locale) {
        let titles: string[] = [];
        let texts: string[] = [];

        const localized = translationResults.value.filter(
          (t) => t.locale === Locales[locale],
        );

        for (let page = 0; page < localized.length; ++page) {
          titles.push(localized[page].title);
          texts.push(localized[page].text.replaceAll("<NL>", "\r\n"));
        }

        translations.push({ locale: Locales[locale], titles, texts });
      }

      setCurrentLocale(LocaleModel.English);
      setTranslations(translations);
    }

    loadTranslations();
  }, [attributeWindows]);

  const handleLocaleChanged = (localeString: string) => {
    const localeId = parseInt(localeString, 10);

    if (isNaN(localeId)) {
      setCurrentLocale(undefined);
    } else {
      setCurrentLocale(localeId);
    }
  };

  return (
    <Modal show={true} onHide={() => onClose()} size="lg">
      <Modal.Header>
        <Row style={{ width: "100%" }}>
          <Col>
            <Modal.Title>View translations</Modal.Title>
          </Col>
          <Col className="col-md-auto">
            <Form.Select
              defaultValue={LocaleModel.English}
              style={{ width: "auto" }}
              onChange={(e) => handleLocaleChanged(e.target.value)}
              disabled={loading}
            >
              <option value={undefined}>Template</option>
              <option value={LocaleModel.English}>
                {LocaleModel[LocaleModel.English]} (default)
              </option>
              {Locales.filter((l) => l !== LocaleModel.English).map((l) => (
                <option key={l} value={l}>
                  {LocaleModel[l]}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </Modal.Header>
      <Modal.Body>
        <ErrorDisplay error={error} />
        {loading && (
          <Spinner
            size="sm"
            variant="primary"
            style={{ position: "absolute", marginRight: "1rem", right: 0 }}
          />
        )}
        <Tabs>
          {attributeWindows.map((aw, index) => (
            <Tab title={`Page ${index + 1}`} eventKey={index} key={index}>
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={
                    translations.find((t) => t.locale === currentLocale)
                      ?.titles[index] ?? ""
                  }
                  readOnly={true}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  value={
                    translations.find((t) => t.locale === currentLocale)?.texts[
                      index
                    ] ?? ""
                  }
                  readOnly={true}
                  rows={15}
                />
              </Form.Group>
            </Tab>
          ))}
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" onClick={(e) => onClose()}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TranslationModal;
